import React from 'react';
import { Skeleton } from './Skeleton';

// Page-shaped skeletons that mirror the real page's layout closely enough
// that the layout doesn't shift when real data arrives. Per Emil:
// "Elements appearing or disappearing without transition feel broken".
// We render these in place of the full-screen LoadingScreen so users
// land on something page-shaped instead of a red void.

export function DashboardSkeleton() {
    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="space-y-2">
                <Skeleton className="h-8 w-48" />
                <Skeleton className="h-4 w-64" />
            </div>
            {/* Quick actions row */}
            <div className="flex flex-wrap gap-2">
                {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-9 w-32" rounded="rounded-lg" />
                ))}
            </div>
            {/* Stat cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="bg-white rounded-xl border border-slate-200 p-5 space-y-2">
                        <Skeleton className="h-3 w-20" />
                        <Skeleton className="h-8 w-12" />
                        <Skeleton className="h-3 w-16" />
                    </div>
                ))}
            </div>
            {/* Revenue row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="bg-white rounded-xl border border-slate-200 p-5 space-y-2">
                        <Skeleton className="h-3 w-24" />
                        <Skeleton className="h-6 w-20" />
                    </div>
                ))}
            </div>
            {/* Two-up panel */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {Array.from({ length: 2 }).map((_, i) => (
                    <div key={i} className="bg-white rounded-xl border border-slate-200 p-6 space-y-3">
                        <Skeleton className="h-5 w-36" />
                        {Array.from({ length: 3 }).map((__, j) => (
                            <Skeleton key={j} className="h-12 w-full" rounded="rounded-lg" />
                        ))}
                    </div>
                ))}
            </div>
            {/* Week overview */}
            <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
                <Skeleton className="h-5 w-32" />
                <div className="grid grid-cols-7 gap-3">
                    {Array.from({ length: 7 }).map((_, i) => (
                        <Skeleton key={i} className="h-20 w-full" rounded="rounded-lg" />
                    ))}
                </div>
            </div>
        </div>
    );
}

export function ClientsSkeleton() {
    return (
        <div className="p-6 space-y-4">
            <div className="flex items-center justify-between">
                <Skeleton className="h-8 w-32" />
                <Skeleton className="h-9 w-32" rounded="rounded-lg" />
            </div>
            <Skeleton className="h-10 w-full" rounded="rounded-lg" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {Array.from({ length: 9 }).map((_, i) => (
                    <div key={i} className="bg-white border border-slate-200 rounded-lg p-4 space-y-2">
                        <Skeleton className="h-5 w-3/4" />
                        <Skeleton className="h-3 w-1/2" />
                        <Skeleton className="h-3 w-2/3" />
                        <div className="flex gap-2 pt-1">
                            <Skeleton className="h-6 w-16" rounded="rounded-full" />
                            <Skeleton className="h-6 w-20" rounded="rounded-full" />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export function EstimatesSkeleton() {
    return (
        <div className="p-6 space-y-4">
            <div className="flex items-center justify-between">
                <Skeleton className="h-8 w-32" />
                <Skeleton className="h-9 w-36" rounded="rounded-lg" />
            </div>
            <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
                {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="p-4 flex items-center gap-4">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-4 w-20" />
                        <div className="flex-1" />
                        <Skeleton className="h-6 w-20" rounded="rounded-full" />
                        <Skeleton className="h-4 w-16" />
                    </div>
                ))}
            </div>
        </div>
    );
}

export function DispatchSkeleton() {
    return (
        <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
                <Skeleton className="h-8 w-40" />
                <div className="flex gap-2">
                    <Skeleton className="h-9 w-24" rounded="rounded-lg" />
                    <Skeleton className="h-9 w-24" rounded="rounded-lg" />
                </div>
            </div>
            {/* 7-day grid: crew column + 7 day columns */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                {/* header row */}
                <div className="grid" style={{ gridTemplateColumns: '160px repeat(7, 1fr)' }}>
                    <div className="p-3 bg-slate-50 border-b border-slate-200">
                        <Skeleton className="h-4 w-20" />
                    </div>
                    {Array.from({ length: 7 }).map((_, i) => (
                        <div key={i} className="p-3 bg-slate-50 border-b border-l border-slate-200">
                            <Skeleton className="h-4 w-12" />
                        </div>
                    ))}
                </div>
                {/* crew rows */}
                {Array.from({ length: 5 }).map((_, row) => (
                    <div key={row} className="grid" style={{ gridTemplateColumns: '160px repeat(7, 1fr)' }}>
                        <div className="p-3 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
                            <Skeleton className="h-6 w-6" rounded="rounded-full" />
                            <Skeleton className="h-3 w-20" />
                        </div>
                        {Array.from({ length: 7 }).map((__, col) => (
                            <div key={col} className="p-2 border-b border-l border-slate-200 min-h-[80px]">
                                {/* leave random cells empty for realism */}
                                {(row + col) % 3 === 0 && (
                                    <Skeleton className="h-6 w-full" rounded="rounded" />
                                )}
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
}

/** Generic fallback for pages we haven't built a tailored skeleton for. */
export function GenericPageSkeleton() {
    return (
        <div className="p-6 space-y-4">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-64" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
                {Array.from({ length: 6 }).map((_, i) => (
                    <Skeleton key={i} className="h-24 w-full" rounded="rounded-xl" />
                ))}
            </div>
        </div>
    );
}
